import Chart from 'chart.js/auto';
import {AlgoBase} from "./AlgoBase";
import userCombinations from "/data/user_combinations.json";

export class Occam1ChartJS extends AlgoBase {

  constructor(target) {
    super("occam", target);
    this.init();
  }

  async init() {

    this.algortihmData = await this.buildDataset("data/algorithms/Occam1.csv");

    const ctx = this.$widget.find("canvas");

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.algortihmData.labels,
        datasets: [{
          label: 'SAT Setpoint', // [dynamic value in loadDataset()],
          data: this.algortihmData.mainDatasets[0],
          borderColor: '#000000',
        }, {
          label: 'Average Space Setpoint Deviation %', // [dynamic value in loadDataset()],
          data: this.algortihmData.averageDatasets[0],
          borderColor: '#EB2B31',
          yAxisID: 'yRight',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              padding: 18,
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                title: "Maximum reset", // [dynamic value in loadDataset()],
                yMin: 0, // [dynamic value in loadDataset()],
                yMax: 0, // [dynamic value in loadDataset()],
                borderColor: '#EB2B31',
                borderDash: [4, 4],
                drawTime: 'beforeDraw',
                z: 1,
              },
              line2: {
                type: 'line',
                title: "Minimum reset", // [dynamic value in loadDataset()],
                yMin: 0,// [dynamic value in loadDataset()],
                yMax: 0,// [dynamic value in loadDataset()],
                borderColor: '#000000',
                borderDash: [4, 4],
                drawTime: 'beforeDraw',
                z: 1,
              },
              box1: {
                type: 'box',
                borderWidth: 0,
                xMin: 36,
                xMax: null,
                // yMin: [dynamic value in loadDataset()],
                // yMax: [dynamic value in loadDataset()],
                backgroundColor: 'rgba(255, 42, 42, 0.3)',
                drawTime: 'beforeDraw',
                z: 0,
                label: {
                  display: true,
                  position: 'center',
                  drawTime: 'afterDraw',
                  z: 2,
                  rotation: -90,
                  content: "Occupied", // [dynamic value in loadDataset()],
                  color: '#000000',
                  font: Chart.defaults.plugins.annotation.common.label.font,
                  beforeDraw: (context, rect) => this.drawLabelBackground(context, rect, 'rgb(245 182 183 / 0.80)')
                },
              },
            },
          },
          title: {
            display: true,
            text: 'OAT', // [dynamic value in loadDataset()],
          }
        },
        elements: {
          line: {
            tension: 0.3,
            borderColor: '#330808',
          }
        },
        scales: {
          x: this.setupXAxis(this.algortihmData.labels.length / 3),
          y: {
            // min: [dynamic value in loadDataset()],
            // max: [dynamic value in loadDataset()],
            ticks: {
              callback: (value, index, ticks) => {
                return this.temperatureLegend(value); // Append the unit
              }
            },
          },
          yRight: {
            position: 'right',
            min: -4,
            max: 4,
            grid: {drawOnChartArea: false},
            ticks: {
              color: '#EB2B31',
              callback: (value, index, ticks) => {
                return this.temperatureLegend(value); // Append the unit
              }
            },
          },
        },
        layout: {
          padding: {
            left: 31,
            right: 5,
          }
        }
      }
    });

    this.loadDataset(0)

    this.initEvents();

    this.ready();
  }

  loadDataset(datasetIndex) {
    const offset = (this.aiEnabled ? 0 : this.getLoopIndex());

    // Update data for preferred unit
    this.algortihmData.mainDatasets[datasetIndex + offset] = this.convertDatasetToPreferredUnit(this.algortihmData.allDatasets[0][datasetIndex + offset])
    this.chart.data.datasets[1].data = this.algortihmData.averageDatasets[datasetIndex + offset];

    // Update y limits for preferred unit
    if (this.preferredUnit === "celsius") {
      this.chart.options.scales.y.min = 10
      this.chart.options.scales.y.max = 20
      this.chart.options.plugins.annotation.annotations.line1.yMin = this.fahrenheitToCelsius(65)
      this.chart.options.plugins.annotation.annotations.line2.yMin = this.fahrenheitToCelsius(55)
    } else {
      this.chart.options.scales.y.min = 52
      this.chart.options.scales.y.max = 67
      this.chart.options.plugins.annotation.annotations.line1.yMin = 65
      this.chart.options.plugins.annotation.annotations.line2.yMin = 55
    }

    // Synchronize annotations height with y limits
    this.chart.options.plugins.annotation.annotations.line1.yMax = this.chart.options.plugins.annotation.annotations.line1.yMin
    this.chart.options.plugins.annotation.annotations.line2.yMax = this.chart.options.plugins.annotation.annotations.line2.yMin

    // Keep annotation lines horizontal (flat)
    this.chart.options.plugins.annotation.annotations.box1.yMin = this.chart.options.scales.y.min
    this.chart.options.plugins.annotation.annotations.box1.yMax = this.chart.options.scales.y.max

    // Synchronize annotations height with y limits
    this.chart.options.plugins.annotation.annotations.box1.yMin = this.chart.options.scales.y.min
    this.chart.options.plugins.annotation.annotations.box1.yMax = this.chart.options.scales.y.max

    // Update dynamic strings from CMS
    this.chart.data.datasets[0].label = this.getLegendFromCMS(1, 1);
    this.chart.data.datasets[1].label = this.getLegendFromCMS(2, 1);
    this.chart.options.plugins.annotation.annotations.line1.title = this.getLegendFromCMS(3, 1);
    this.chart.options.plugins.annotation.annotations.line2.title = this.getLegendFromCMS(4, 1);
    const userCombinationData = userCombinations[this.algorithmName][datasetIndex];
    this.chart.options.plugins.annotation.annotations.box1.label.content = this.getLegendFromCMS(5, 1).toUpperCase().replace("%", userCombinationData.title);

    this.chart.options.plugins.title.text = this.getLegendFromCMS(6, 1)?.toUpperCase();

    super.loadDataset(datasetIndex);
  }

  async buildDataset(filename) {
    const singleDataSetBuild = await super.buildDatasetFromCSV(filename, 2);
    return {
      labels: singleDataSetBuild.labels,
      allDatasets: singleDataSetBuild.allDatasets,
      mainDatasets: [...singleDataSetBuild.allDatasets[0]],
      averageDatasets: [...singleDataSetBuild.allDatasets[1]],
    };
  }

}
